import { useState } from 'react';
import { useLocation, useRoute } from 'wouter';
import useSWR from 'swr';
import {
    ArrowLeft,
    Search,
    Filter,
    ArrowUpRight,
    ArrowDownLeft,
    Calendar,
    Package,
    AlertCircle
} from 'lucide-react';
import { useAuthStore } from '../hooks/useAuth';
import { TableSkeleton } from '../components/ui/TableSkeleton';
import { PODetailsDrawer } from '../components/PODetailsDrawer';

interface Customer {
    id: string;
    name: string;
    code?: string;
    status: string;
}

interface PO {
    id: string;
    customerPoNumber: string;
    clientName: string;
    type: 'inbound' | 'outbound';
    status: 'open' | 'closed';
    totalSkids?: number;
    carrierEmail?: string;
    description?: string;
    createdAt?: string;
    updatedAt?: string;
}

interface OrdersResponse {
    customer: Customer;
    pos: PO[];
}

export function CustomerOrders() {
    const [match, params] = useRoute('/customers/:id/orders');
    const [, setLocation] = useLocation();
    const { token } = useAuthStore();

    // Filters
    const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc');

    // Selection
    const [selectedPO, setSelectedPO] = useState<PO | null>(null);

    const customerId = match ? params?.id : null;

    // Fetch Key
    const fetchUrl = customerId && token
        ? `/api/customers/${customerId}/orders?status=${statusFilter}&sort=${sortDirection === 'desc' ? 'newest' : 'oldest'}&q=${encodeURIComponent(searchQuery)}`
        : null;

    const { data, error, isLoading, mutate } = useSWR<OrdersResponse>(
        fetchUrl,
        async (url: string) => {
            const res = await fetch(url, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.error || 'Failed to fetch orders');
            }
            return res.json();
        }
    );

    const handleRowClick = (po: PO) => {
        // Only open if logic allows (prevent text selection trigger if needed, but simple click is fine)
        if (!window.getSelection()?.toString()) {
            setSelectedPO(po);
        }
    };

    if (error) {
        return (
            <div className="p-8 flex flex-col items-center justify-center h-full text-center">
                <div className="p-4 rounded-full bg-red-500/10 text-red-400 mb-4">
                    <AlertCircle size={32} />
                </div>
                <h2 className="text-xl font-bold mb-2">Could not load orders</h2>
                <p className="text-muted-foreground max-w-md mb-6">{error.message}</p>
                <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                >
                    Retry
                </button>
                <div className="mt-8">
                    <button onClick={() => setLocation('/customers')} className="text-sm text-muted-foreground hover:text-foreground">
                        ← Back to Customers
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-[1600px] mx-auto flex flex-col min-h-0 h-full">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8 shrink-0">
                <button
                    onClick={() => setLocation('/customers')}
                    className="p-2 hover:bg-muted rounded-full text-muted-foreground transition-colors"
                >
                    <ArrowLeft size={20} />
                </button>

                <div>
                    <h1 className="text-2xl font-bold font-outfit tracking-tight flex items-center gap-3">
                        {data?.customer ? `${data.customer.name} Orders` : (isLoading ? 'Loading Customer...' : 'Customer Orders')}
                    </h1>
                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                        {data?.customer?.code && <span className="font-mono bg-muted/50 px-1.5 py-0.5 rounded text-xs">{data.customer.code}</span>}
                        <span>Historical Purchase Orders</span>
                    </div>
                </div>
            </div>

            {/* Toolbar */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6 shrink-0">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search PO #, Client, Description..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg focus:ring-1 focus:ring-primary focus:outline-none transition-all"
                    />
                </div>

                <div className="flex gap-2">
                    <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2">
                        <Filter size={14} className="text-muted-foreground" />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as any)}
                            className="bg-transparent border-none text-sm font-medium focus:outline-none"
                        >
                            <option value="all">Status: All</option>
                            <option value="open">Status: Open</option>
                            <option value="closed">Status: Closed</option>
                        </select>
                    </div>

                    <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-3 py-2">
                        <Calendar size={14} className="text-muted-foreground" />
                        <select
                            value={sortDirection}
                            onChange={(e) => setSortDirection(e.target.value as any)}
                            className="bg-transparent border-none text-sm font-medium focus:outline-none"
                        >
                            <option value="desc">Newest First</option>
                            <option value="asc">Oldest First</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto rounded-xl border border-border bg-card/50 backdrop-blur-sm relative">
                {isLoading && !data ? (
                    <TableSkeleton />
                ) : (
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-muted/50 sticky top-0 z-10 backdrop-blur-md">
                            <tr>
                                <th className="p-4 font-semibold text-sm text-muted-foreground">PO Number</th>
                                <th className="p-4 font-semibold text-sm text-muted-foreground">Status</th>
                                <th className="p-4 font-semibold text-sm text-muted-foreground">Type</th>
                                <th className="p-4 font-semibold text-sm text-muted-foreground">Logistics</th>
                                <th className="p-4 font-semibold text-sm text-muted-foreground">Created</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {data?.pos.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className="p-12 text-center text-muted-foreground">
                                        <div className="flex flex-col items-center gap-2">
                                            <Package size={32} className="opacity-20" />
                                            <p>No orders found matching your filters.</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                data?.pos.map((po) => (
                                    <tr
                                        key={po.id}
                                        onClick={() => handleRowClick(po)}
                                        className="hover:bg-muted/30 transition-colors group cursor-pointer"
                                    >
                                        <td className="p-4 font-medium font-mono text-foreground">
                                            {po.customerPoNumber}
                                            {po.clientName !== data.customer.name && (
                                                <div className="text-xs text-muted-foreground font-sans mt-0.5 truncate max-w-[200px]" title={po.clientName}>
                                                    Via: {po.clientName}
                                                </div>
                                            )}
                                        </td>
                                        <td className="p-4">
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${po.status === 'open'
                                                ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                                                : 'bg-slate-500/10 text-slate-400 border-slate-500/20'
                                                }`}>
                                                {po.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td className="p-4">
                                            <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium border ${po.type === 'inbound'
                                                ? 'text-emerald-400 border-emerald-500/20 bg-emerald-500/5'
                                                : 'text-blue-400 border-blue-500/20 bg-blue-500/5'
                                                }`}>
                                                {po.type === 'inbound' ? <ArrowDownLeft size={10} /> : <ArrowUpRight size={10} />}
                                                {po.type}
                                            </span>
                                        </td>
                                        <td className="p-4 text-sm text-muted-foreground">
                                            {po.totalSkids ? `${po.totalSkids} Skids` : '-'}
                                        </td>
                                        <td className="p-4 text-sm text-muted-foreground font-mono">
                                            {po.createdAt ? new Date(po.createdAt).toLocaleDateString() : 'N/A'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Detail Drawer */}
            <PODetailsDrawer
                po={selectedPO}
                open={!!selectedPO}
                onClose={() => setSelectedPO(null)}
                // Optimistic update or refetch
                onUpdate={() => mutate()}
            />
        </div>
    );
}
